import { Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import React, { FC, useState, useContext } from 'react';
import * as yup from 'yup';
import alphanumeric from '../../../helpers/alphanumeric';
import usePushFormikFormToDataLayer from '../../../hooks/use-push-formik-form-to-data-layer/use-push-formik-form-to-data-layer';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import { ForgottenDetailsType } from '../../../models/forgotten-details-type';
import FormErrorTypeEnum from '../../../models/form-error-type-enum';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import accountPasswordResetEmail from '../../../services/customer-api/endpoints/account/password-reset-email';
import accountUsernameEmail from '../../../services/customer-api/endpoints/account/username-email';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import FormText from '../../atoms/form-text/form-text';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import TextLink from '../../atoms/text-link/text-link';
import FormRadioGroup from '../../molecules/form-radio-group/form-radio-group';

const ForgottenDetails: FC<ForgottenDetailsType> = ({
  buttonText,
  confirmationDescription,
  confirmationHeading,
  description,
  emailAddressFieldDescription,
  emailAddressFieldLabel,
  emailAddressFormatInvalid,
  emailAddressMandatory,
  emailAddressOrUsernameFieldDescription,
  emailAddressOrUsernameFieldLabel,
  emailAddressOrUsernameMandatory,
  forgotPasswordEmailResetUrl,
  heading,
  loginButtonText,
  passwordRadioButtonLabel,
  returnToLoginPageUrl,
  usernameRadioButtonLabel,
}: ForgottenDetailsType) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [formSuccessfullySubmitted, setFormSuccessfullySubmitted] =
    useState<boolean>(null);
  const { alphanumericFormat, alphanumericValidationMessage } = alphanumeric();
  const isSuccessSpacing = isSuccess
    ? 'pb-8'
    : 'pb-component-spacing-mobile lg:pb-component-spacing';

  const formContext = usePushFormikFormToDataLayer(
    'Forgotten details',
    formSuccessfullySubmitted
  );

  return (
    <div className="py-component-spacing-mobile lg:py-component-spacing">
      <h1 className="max-w-2xl mx-auto page-heading-two text-center">
        {isSuccess ? confirmationHeading : heading}
      </h1>

      {isSuccess && (
        <motion.div
          initial={{ scale: 0 }}
          className="flex justify-center my-4"
          animate={{ scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <IcomoonIcon
            name="check"
            className="p-4 text-6xl rounded-full bg-turquoise-light text-white"
          />
        </motion.div>
      )}

      <p
        className={
          `${isSuccessSpacing} ` +
          'max-w-md mx-auto pt-4 text-center text-grey-600'
        }
      >
        {isSuccess ? confirmationDescription : description}
      </p>

      {isSuccess && (
        <div className="flex justify-center">
          <ButtonPrimary name={buttonText} url="/" />
        </div>
      )}

      {!isSuccess && (
        <div className="mx-auto w-full md:w-96">
          <Formik
            innerRef={formContext}
            initialValues={{
              identifier: '',
              type: 'username',
            }}
            onSubmit={async ({ type, identifier }) => {
              setIsLoading(true);

              try {
                if (type === 'username') {
                  await accountUsernameEmail(identifier);

                  pushToDataLayer(GtmEventNameEnum.LoginForgotUsername);
                } else {
                  await accountPasswordResetEmail(
                    identifier,
                    forgotPasswordEmailResetUrl.url
                  );

                  pushToDataLayer(GtmEventNameEnum.LoginForgotPassword);
                }

                setIsSuccess(true);
                setIsLoading(false);
                setFormSuccessfullySubmitted(true);
                window.scrollTo({ top: 0 });
              } catch (error) {
                if (error?.response?.status === 404) {
                  setIsSuccess(true);
                  setIsLoading(false);
                  setFormSuccessfullySubmitted(true);
                  window.scrollTo({ top: 0 });

                  return;
                }

                setFormSuccessfullySubmitted(false);
                setIsLoading(false);
              }
            }}
            validationSchema={yup.object().shape({
              identifier: yup
                .string()
                .max(60)
                .matches(alphanumericFormat, alphanumericValidationMessage)
                .when('type', {
                  is: 'username',
                  then: yup
                    .string()
                    .email(emailAddressFormatInvalid)
                    .required(emailAddressMandatory)
                    .label(emailAddressFieldLabel),
                })
                .when('type', {
                  is: 'password',
                  then: yup
                    .string()
                    .required(emailAddressOrUsernameMandatory)
                    .trim(emailAddressOrUsernameMandatory)
                    .label(emailAddressOrUsernameFieldLabel),
                }),
            })}
          >
            {({ values }) => (
              <Form>
                <div className="flex space-y-8 flex-col items-stretch">
                  <FormRadioGroup
                    className="mx-auto"
                    layout="horizontal"
                    name="type"
                    options={[
                      { value: 'username', label: usernameRadioButtonLabel },
                      { value: 'password', label: passwordRadioButtonLabel },
                    ]}
                  />

                  <p className="max-w-sm text-center text-grey-600 text-sm">
                    {values.type === 'username'
                      ? emailAddressFieldDescription
                      : emailAddressOrUsernameFieldDescription}
                  </p>

                  {values.type === 'username' && (
                    <FormText
                      name="identifier"
                      type="email"
                      label={
                        values.type === 'username'
                          ? emailAddressFieldLabel
                          : emailAddressOrUsernameFieldLabel
                      }
                      placeholder={
                        values.type === 'username'
                          ? emailAddressFieldLabel
                          : emailAddressOrUsernameFieldLabel
                      }
                    />
                  )}

                  {values.type !== 'username' && (
                    <FormText
                      name="identifier"
                      type="text"
                      label={
                        values.type === 'username'
                          ? emailAddressFieldLabel
                          : emailAddressOrUsernameFieldLabel
                      }
                      placeholder={
                        values.type === 'username'
                          ? emailAddressFieldLabel
                          : emailAddressOrUsernameFieldLabel
                      }
                    />
                  )}

                  {formSuccessfullySubmitted === false && (
                    <span className="block mt-1 text-sm text-invalid">
                      {FormErrorTypeEnum.Unknown}
                    </span>
                  )}

                  <div className="flex flex-col space-y-8 items-center">
                    <ButtonPrimary
                      name={loginButtonText}
                      disabled={isLoading}
                      buttonType="submit"
                    />

                    <TextLink
                      {...returnToLoginPageUrl}
                      className="inline-block mx-auto"
                      onClick={() =>
                        pushToDataLayer(GtmEventNameEnum.LoginForgotCancel)
                      }
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default ForgottenDetails;
